import type { SxProps, Theme } from "@mui/material";

// How tight the recipe form's controls are, in one place so the labelled field
// cards and the unlabelled rolls cells stay in proportion with each other.

interface Density {
	fontSize: string;
	py: string;
	px: string;
	/** Omit for controls with no floating label (the rolls cells). */
	labelSize?: string;
	/** Numbers are right-aligned; a select's text is not. */
	numeric?: boolean;
}

const build = ({
	fontSize,
	py,
	px,
	labelSize,
	numeric,
}: Density): SxProps<Theme> => ({
	"& .MuiOutlinedInput-input": {
		fontSize,
		paddingTop: py,
		paddingBottom: py,
		paddingLeft: px,
		paddingRight: px,
		...(numeric
			? {
					// House rule: numbers read right-aligned, and tabular figures hold
					// the digits in a column so two values compare by shape.
					textAlign: "right",
					fontVariantNumeric: "tabular-nums",
				}
			: {}),
	},
	...(labelSize
		? {
				"& .MuiInputLabel-root": { fontSize: labelSize },
				// The notch is cut by the legend, and the legend does not inherit the
				// label's size — it has to be set to the same 0.75 the shrunk label is
				// scaled by, or the gap stops matching the text sitting in it.
				"& .MuiOutlinedInput-notchedOutline legend": {
					fontSize: `calc(${labelSize} * 0.75)`,
				},
				"& .MuiFormHelperText-root": {
					fontSize: "0.65rem",
					marginTop: "2px",
					marginLeft: "4px",
				},
			}
		: {}),
});

const FIELD_DENSITY = { fontSize: "0.8rem", py: "5px", px: "8px" } as const;

// A labelled field inside a group card.
export const FIELD_SX = build({ ...FIELD_DENSITY, labelSize: "0.75rem" });
export const NUMERIC_FIELD_SX = build({
	...FIELD_DENSITY,
	labelSize: "0.75rem",
	numeric: true,
});

// A rolls-table cell: no label, and five of them sit across the column.
export const ROLL_CELL_SX = build({
	fontSize: "0.75rem",
	py: "3px",
	px: "6px",
	numeric: true,
});

// Field column module. The floating label is what sets it, not the value: MUI
// ellipsizes a shrunk label past `133% - 32px` of the field, and the longest
// name in the catalog ("CCL Mag Magnetization") has to survive that.
export const FIELD_WIDTH = 124;
